import { useState, useEffect, FormEvent, useCallback } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { localizedApiMessage, type LocalizedMessage } from '../lib/api-error-message';
import { apiClient, ViewerFullSignupRequest } from '../lib/api-client';
import LanguageSwitcher from '../components/LanguageSwitcher';
import GuideLink from '../components/GuideLink';
import { ROUTES, STORAGE_KEYS } from '../lib/constants';

// セッションストレージのキー
const FORM_STORAGE_KEY = STORAGE_KEYS.REGISTER_FORM;

// 職業リスト（キーのみ - 翻訳は i18n で取得）
const OCCUPATION_KEYS = [
  'employee', 'publicServant', 'selfEmployed', 'executive', 'professional', 'partTime',
  'student', 'homemaker', 'unemployed', 'other'
];

interface FormData {
  email: string;
  password: string;
  passwordConfirm: string;
  fullName: string;
  fullNameKana: string;
  companyName: string;
  birthYear: string;
  birthMonth: string;
  birthDay: string;
  gender: string;
  occupation: string;
  termsAgreed: boolean;
}

interface FormErrors {
  [key: string]: { key: string };
}

// 共通のフォームフィールドコンポーネント
interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: { key: string };
  children: React.ReactNode;
  hint?: string;
}

function FormField({ label, required, error, children, hint }: FormFieldProps) {
  const { t } = useTranslation('auth');
  return (
    <tr className="border-b border-gray-100 last:border-b-0">
      <th className="py-3 pr-4 text-left align-top w-40 whitespace-nowrap">
        <span className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
        {hint && <span className="block text-xs text-gray-400 font-normal mt-0.5">{hint}</span>}
      </th>
      <td className="py-3">
        {children}
        {error && <p className="mt-1 text-sm text-red-500">{t(error.key)}</p>}
      </td>
    </tr>
  );
}

// レスポンシブ対応のフォームセクション
interface FormSectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

function FormSection({ title, subtitle, children }: FormSectionProps) {
  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
        <h2 className="text-base font-semibold text-gray-800">
          {title}
          {subtitle && <span className="text-sm font-normal text-gray-500 ml-2">{subtitle}</span>}
        </h2>
      </div>
      <div className="px-4 sm:px-6 py-2">
        <table className="w-full">
          <tbody>
            {children}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// セッションストレージから初期値を取得（パスワードは除外）
const getInitialFormData = (): FormData => {
  const defaultData: FormData = {
    email: '',
    password: '',
    passwordConfirm: '',
    fullName: '',
    fullNameKana: '',
    companyName: '',
    birthYear: '',
    birthMonth: '',
    birthDay: '',
    gender: '',
    occupation: '',
    termsAgreed: false,
  };

  try {
    const saved = sessionStorage.getItem(FORM_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // パスワードは復元しない（セキュリティ上の理由）
      return {
        ...defaultData,
        ...parsed,
        password: '',
        passwordConfirm: '',
      };
    }
  } catch {
    // パースエラーの場合はデフォルト値を使用
  }
  return defaultData;
};

export default function Register() {
  const { t, i18n } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { t: tGuide } = useTranslation('guide');
  const isJapanese = i18n.language === 'ja';
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<LocalizedMessage | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const [formData, setFormData] = useState<FormData>(getInitialFormData);

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // フォームデータをセッションストレージに保存（パスワードは除外）
  const saveFormData = useCallback((data: FormData) => {
    try {
      const dataToSave = {
        ...data,
        password: '', // パスワードは保存しない
        passwordConfirm: '', // パスワードは保存しない
      };
      sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(dataToSave));
    } catch {
      // ストレージエラーは無視
    }
  }, []);

  // フォームデータが変更されたらセッションストレージに保存
  useEffect(() => {
    saveFormData(formData);
  }, [formData, saveFormData]);

  // 登録成功時にセッションストレージをクリア
  const clearFormStorage = useCallback(() => {
    try {
      sessionStorage.removeItem(FORM_STORAGE_KEY);
    } catch {
      // エラーは無視
    }
  }, []);

  // 年の選択肢を生成（現在年から100年前まで。年齢判定は送信時に行う）
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 101 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  // フォームバリデーション
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    // メールアドレス
    if (!formData.email) {
      errors.email = { key: 'register.validation.emailRequired' };
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = { key: 'register.validation.emailInvalid' };
    }

    // パスワード
    if (!formData.password) {
      errors.password = { key: 'register.validation.passwordRequired' };
    } else if (formData.password.length < 8) {
      errors.password = { key: 'register.validation.passwordTooShort' };
    }

    // パスワード確認
    if (formData.password !== formData.passwordConfirm) {
      errors.passwordConfirm = { key: 'register.validation.passwordMismatch' };
    }

    // 氏名
    if (!formData.fullName) {
      errors.fullName = { key: 'register.validation.fullNameRequired' };
    }

    // 氏名（カナ）- 日本語モードのみ必須
    if (isJapanese) {
      if (!formData.fullNameKana) {
        errors.fullNameKana = { key: 'register.validation.fullNameKanaRequired' };
      } else if (!/^[ァ-ヶー\s]+$/.test(formData.fullNameKana)) {
        errors.fullNameKana = { key: 'register.validation.fullNameKanaInvalid' };
      }
    }

    // 生年月日（必須・18歳以上）
    if (!formData.birthYear || !formData.birthMonth || !formData.birthDay) {
      errors.birthDate = { key: 'register.validation.birthDateRequired' };
    } else {
      const year = Number(formData.birthYear);
      const month = Number(formData.birthMonth);
      const day = Number(formData.birthDay);
      const birthDate = new Date(year, month - 1, day);
      if (birthDate.getFullYear() !== year || birthDate.getMonth() !== month - 1 || birthDate.getDate() !== day) {
        errors.birthDate = { key: 'register.validation.birthDateInvalid' };
      } else {
        const today = new Date();
        let age = today.getFullYear() - year;
        if (today.getMonth() < month - 1 || (today.getMonth() === month - 1 && today.getDate() < day)) age -= 1;
        if (age < 18) errors.birthDate = { key: 'register.validation.ageRestriction' };
      }
    }

    // 利用規約
    if (!formData.termsAgreed) {
      errors.termsAgreed = { key: 'register.validation.termsRequired' };
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // フォーム送信
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // 生年月日を組み立て
      const birthDate = `${formData.birthYear}-${formData.birthMonth.padStart(2, '0')}-${formData.birthDay.padStart(2, '0')}`;

      const signupData: ViewerFullSignupRequest = {
        email: formData.email,
        password: formData.password,
        full_name: formData.fullName,
        full_name_kana: isJapanese ? formData.fullNameKana : undefined,
        company_name: formData.companyName || undefined,
        birth_date: birthDate,
        gender: formData.gender || undefined,
        occupation: formData.occupation || undefined,
        terms_agreed: formData.termsAgreed,
      };

      const response = await apiClient.viewerFullSignup(signupData);

      if (response) {
        // 登録成功時にセッションストレージをクリア
        clearFormStorage();
        setSuccess(true);
        // 3秒後にメール確認ページへ遷移
        setTimeout(() => {
          navigate(ROUTES.VERIFY_EMAIL, { state: { email: formData.email } });
        }, 3000);
      }
    } catch (err: unknown) {
      setError(localizedApiMessage(err, 'apiErrors.registerFailed'));
      // エラー時は画面上部にスクロール
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  // 入力値更新
  const updateField = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // エラーをクリア
    if (formErrors[field]) {
      setFormErrors(prev => {
        const { [field]: _cleared, ...remainingErrors } = prev;
        return remainingErrors;
      });
    }
  };

  // 入力フィールドの共通スタイル
  const inputClass = (hasError: boolean) =>
    `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors ${
      hasError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
    }`;

  const selectClass = (hasError: boolean) =>
    `px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors bg-white ${
      hasError ? 'border-red-400 bg-red-50' : 'border-gray-300'
    }`;

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('register.successTitle')}</h2>
            <p className="text-gray-600 mb-4">
              {t('register.successMessage')}
            </p>
            <p className="text-sm text-gray-400">
              {t('register.redirecting')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Language Switcher */}
        <div className="flex justify-end mb-4">
          <LanguageSwitcher variant="buttons" />
        </div>

        {/* ヘッダー */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('register.title')}</h1>
          <p className="text-gray-500 mt-2">{t('register.subtitle')}</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-700">{tCommon(error.key, error.values)}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ログイン情報 */}
          <FormSection title={t('register.sections.loginInfo')}>
            <FormField label={t('register.email')} required error={formErrors.email}>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className={inputClass(!!formErrors.email)}
                placeholder={t('register.placeholders.email')}
              />
            </FormField>

            <FormField label={t('register.password')} required error={formErrors.password} hint={t('register.hints.passwordLength')}>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => updateField('password', e.target.value)}
                  className={`${inputClass(!!formErrors.password)} pr-10`}
                  placeholder={t('register.placeholders.password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? tCommon('password.hide') : tCommon('password.show')}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </FormField>

            <FormField label={t('register.passwordConfirm')} required error={formErrors.passwordConfirm}>
              <div className="relative">
                <input
                  type={showPasswordConfirm ? 'text' : 'password'}
                  value={formData.passwordConfirm}
                  onChange={(e) => updateField('passwordConfirm', e.target.value)}
                  className={`${inputClass(!!formErrors.passwordConfirm)} pr-10`}
                  placeholder={t('register.placeholders.passwordConfirm')}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPasswordConfirm ? tCommon('password.hide') : tCommon('password.show')}
                >
                  {showPasswordConfirm ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </FormField>
          </FormSection>

          {/* お客様情報 */}
          <FormSection title={t('register.sections.customerInfo')}>
            <FormField label={t('register.fields.name')} required error={formErrors.fullName}>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => updateField('fullName', e.target.value)}
                className={inputClass(!!formErrors.fullName)}
                placeholder={t('register.placeholders.fullName')}
              />
            </FormField>

            {isJapanese && (
              <FormField label={t('register.fields.nameKana')} required error={formErrors.fullNameKana} hint={t('register.hints.kanaFormat')}>
                <input
                  type="text"
                  value={formData.fullNameKana}
                  onChange={(e) => updateField('fullNameKana', e.target.value)}
                  className={inputClass(!!formErrors.fullNameKana)}
                  placeholder={t('register.placeholders.fullNameKana')}
                />
              </FormField>
            )}

            <FormField label={t('register.fields.companyName')} hint={t('register.hints.optional')}>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => updateField('companyName', e.target.value)}
                className={inputClass(false)}
                placeholder={t('register.placeholders.companyName')}
              />
            </FormField>
          </FormSection>

          {/* その他の情報 */}
          <FormSection title={t('register.sections.otherInfo')}>
            <FormField label={t('register.fields.birthDate')} required error={formErrors.birthDate}>
              <div className="flex flex-wrap gap-2 items-center">
                <select
                  value={formData.birthYear}
                  onChange={(e) => updateField('birthYear', e.target.value)}
                  className={`w-24 ${selectClass(!!formErrors.birthDate)}`}
                >
                  <option value="">{t('register.select.year')}</option>
                  {years.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <span className="text-gray-500">{t('register.select.year')}</span>
                <select
                  value={formData.birthMonth}
                  onChange={(e) => updateField('birthMonth', e.target.value)}
                  className={`w-20 ${selectClass(!!formErrors.birthDate)}`}
                >
                  <option value="">{t('register.select.month')}</option>
                  {months.map((month) => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
                <span className="text-gray-500">{t('register.select.month')}</span>
                <select
                  value={formData.birthDay}
                  onChange={(e) => updateField('birthDay', e.target.value)}
                  className={`w-20 ${selectClass(!!formErrors.birthDate)}`}
                >
                  <option value="">{t('register.select.day')}</option>
                  {days.map((day) => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
                <span className="text-gray-500">{t('register.select.day')}</span>
              </div>
            </FormField>

            <FormField label={t('register.fields.gender')}>
              <div className="flex flex-wrap gap-4">
                {[
                  { value: 'male', label: t('register.gender.male') },
                  { value: 'female', label: t('register.gender.female') },
                  { value: 'other', label: t('register.gender.other') },
                  { value: 'noAnswer', label: t('register.gender.noAnswer') }
                ].map((option) => (
                  <label key={option.value} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="radio"
                      name="gender"
                      value={option.value}
                      checked={formData.gender === option.value}
                      onChange={(e) => updateField('gender', e.target.value)}
                      className="w-4 h-4 text-primary focus:ring-primary/50 border-gray-300"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">{option.label}</span>
                  </label>
                ))}
              </div>
            </FormField>

            <FormField label={t('register.fields.occupation')}>
              <select
                value={formData.occupation}
                onChange={(e) => updateField('occupation', e.target.value)}
                className={`w-full sm:w-48 ${selectClass(false)}`}
              >
                <option value="">{t('register.select.occupation')}</option>
                {OCCUPATION_KEYS.map((key) => (
                  <option key={key} value={key}>{t(`register.occupations.${key}`)}</option>
                ))}
              </select>
            </FormField>
          </FormSection>

          {/* 利用規約 */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.termsAgreed}
                  onChange={(e) => updateField('termsAgreed', e.target.checked)}
                  className="mt-0.5 w-5 h-5 text-primary focus:ring-primary/50 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                  {t('register.termsAgree.prefix')}
                  <Link
                    to={ROUTES.TERMS}
                    className="text-primary hover:underline font-medium"
                  >
                    {t('register.termsAgree.link')}
                  </Link>
                  {t('register.termsAgree.suffix')}
                  <span className="text-red-500 ml-1">*</span>
                </span>
              </label>
              {formErrors.termsAgreed && (
                <p className="mt-2 text-sm text-red-500 ml-8">{t(formErrors.termsAgreed.key)}</p>
              )}
            </div>
          </section>

          {/* 送信ボタン */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-primary hover:bg-primary-600 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {tCommon('status.loading')}
                </span>
              ) : t('register.submit')}
            </button>
          </div>
        </form>

        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            {t('register.alreadyHaveAccount')}{' '}
            <Link to={ROUTES.LOGIN} className="text-primary hover:underline font-medium">
              {t('register.loginHere')}
            </Link>
          </p>
          <GuideLink chapter="register" className="mt-3 inline-block text-sm font-medium text-primary hover:underline">
            {tGuide('links.register')}
          </GuideLink>
        </div>
      </div>
    </div>
  );
}
